// Library Methods
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InstantSearch, Hits, Highlight, SearchBox } from "react-instantsearch";
import { useTranslation } from "react-i18next";
import algoliasearch from "algoliasearch";
import i18next from "i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import styles from "./index.module.css";
import "./algolia-search.css";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Configs
import { priorityLanguageMap } from "src/config/languages";

type AlgoliaSearchType = {
  isTest?: boolean;
  navbarOptions: Array<{
    route: string;
    label: string;
    subRoutes?: Array<{ route: string; label: string }>;
  }>;
  handleSelectNavbarOption: (option: {
    route: string;
    label: string;
    subRoutes?: Array<{ route: string; label: string }>;
  }) => void;
  handleSelectSubRoute: (
    option: {
      route: string;
      label: string;
    },
    subOption: {
      route: string;
      label: string;
    }
  ) => void;
};

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID ?? "",
  process.env.REACT_APP_ALGOLIA_API_KEY ?? ""
);

const AlgoliaSearch = ({
  isTest = false,
  navbarOptions,
  handleSelectNavbarOption,
  handleSelectSubRoute,
}: AlgoliaSearchType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // The search box and results dropdown's dark mode colors, picked up by
  // index.module.css; in light mode the CSS falls back to its own colors
  const darkModeColors = useMemo(() => {
    if (theme.palette.mode !== "dark") return undefined;

    const {
      surface,
      surfaceHover,
      onSurface,
      outline,
      inputBackground,
      inputBorder,
    } = getSurfaceTokens(theme);
    return {
      "--algolia-surface": surface,
      "--algolia-surface-hover": surfaceHover,
      "--algolia-on-surface": onSurface,
      "--algolia-border": outline,
      "--algolia-divider": outline,
      "--algolia-input-background": inputBackground,
      "--algolia-input-border": inputBorder,
      "--algolia-input-text": onSurface,
      "--algolia-input-placeholder": inputBorder,
    } as React.CSSProperties;
  }, [theme]);

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const [isInputFocused, setIsInputFocused] = useState(isTest);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const transformItems: any = (items: any) => {
    return items;
  };

  const handleClick = useCallback(
    (hit: any) => {
      setIsInputFocused(false);

      // path with main link and sublink
      const path =
        typeof hit?.newPath === "string"
          ? hit.newPath.trim().split("/knowledge-hub/operating-manual/")?.[1]
          : "";

      // it is a main link
      if (!path.includes("/")) {
        const selectedOption = navbarOptions.find(
          (option) => option.route === path
        );

        if (selectedOption) {
          handleSelectNavbarOption(selectedOption);
        }
      }
      // else it is a sublink
      else {
        const mainPath = path.split("/")?.[0];
        const subPath = path.split("/")?.[1];

        const selectedOption = navbarOptions.find(
          (option) => option.route === mainPath
        );

        if (selectedOption) {
          const selectedSubOption = selectedOption.subRoutes?.find(
            (subOption) => subOption.route === subPath
          );

          if (selectedSubOption) {
            handleSelectSubRoute(selectedOption, selectedSubOption);
          }
        }
      }
    },
    [navbarOptions, handleSelectNavbarOption, handleSelectSubRoute]
  );

  useEffect(() => {
    const handleClickOutside: any = (event: any) => {
      if (
        searchContainerRef?.current &&
        !searchContainerRef?.current?.contains(event?.target)
      ) {
        setIsInputFocused(false);
      } else {
        setIsInputFocused(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function Hit({ hit }: { hit: any }) {
    if (!hit) return null;

    return (
      <div
        className={styles.hitItem}
        onClick={() => {
          handleClick(hit);
        }}
        style={{ cursor: "pointer" }}
      >
        <article>
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
              <Highlight attribute="title" hit={hit} />
            </Typography>
            <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
              <Highlight attribute="description" hit={hit} />
            </Typography>
            <Typography sx={{ fontWeight: 300, fontSize: "12px" }}>
              Keywords: [ <Highlight attribute="keywords" hit={hit} /> ]
            </Typography>
          </Box>
        </article>
      </div>
    );
  }

  return (
    <div
      ref={searchContainerRef}
      className={styles.searchContainer}
      style={darkModeColors}
    >
      <InstantSearch
        searchClient={searchClient}
        indexName={
          currentLang === "fr"
            ? process.env.REACT_APP_ALGOLIA_INDEX_NAME_FRENCH
            : process.env.REACT_APP_ALGOLIA_INDEX_NAME_ENGLISH
        }
        insights
      >
        <SearchBox
          classNames={{
            root: `${styles.searchBoxContainer}`,
            input: `${styles.searchInput} px-4 py-3 w-full outline-none transition-all duration-300`,
          }}
          placeholder={t("Search here...")}
        />
        {isInputFocused && (
          <div className={`${styles.searchResultsContainer}`}>
            <Hits hitComponent={Hit} transformItems={transformItems} />
          </div>
        )}
      </InstantSearch>
    </div>
  );
};

export default AlgoliaSearch;
